import type { FC, PropsWithChildren } from 'react';

import { useMemo } from 'react';

import { AAFProductImageProvider } from './AAFProductImageProvider';
import { AmplienceImageProvider } from './AmplienceImageProvider';
import { GStarProductImageProvider } from './GStarProductImageProvider';
import { TileImageDisplay } from './TileImageDisplay';
import type { Image } from '~/amplienceTypes/schemas/exported/product-schema';
import { useTileBaseContext } from '~/components/shared/recommendations/tile/TileBaseContext';
import { useAppContext } from '~/utilities/context/static/AppContext';
import { localisedData } from '~/utilities/helpers';

export interface TileImageProps {
  showHover: boolean;
  override?: Image;
  overrideHovered?: Image;
  index?: number;
}

export const TileImage: FC<PropsWithChildren<TileImageProps>> = ({
  showHover,
  override,
  overrideHovered,
  index,
  children,
}) => {
  const { isGStar, lang } = useAppContext();
  const { singleProduct } = useTileBaseContext();
  // Determine which provider to use based on the presence of override images
  const isAmplienceProvider = !!(override?.override || overrideHovered?.override);

  const name = useMemo(
    () => localisedData(singleProduct, singleProduct?.name, lang) ?? '',
    [singleProduct, lang]
  );

  if (isAmplienceProvider) {
    return (
      <AmplienceImageProvider primaryImage={override} hoverImage={overrideHovered}>
        <TileImageDisplay index={index} alt={name} showHover={showHover}>
          {children}
        </TileImageDisplay>
      </AmplienceImageProvider>
    );
  }

  if (isGStar) {
    return (
      <GStarProductImageProvider product={singleProduct}>
        <TileImageDisplay index={index} alt={name} showHover={showHover}>
          {children}
        </TileImageDisplay>
      </GStarProductImageProvider>
    );
  }

  return (
    <AAFProductImageProvider product={singleProduct}>
      <TileImageDisplay index={index} alt={name} showHover={showHover}>
        {children}
      </TileImageDisplay>
    </AAFProductImageProvider>
  );
};
